import { Button, ImageAtoms } from '@vfit/shared/atoms';
import { purify } from '@vfit/shared/themes';
import { useDeviceType } from '@vfit/shared/hooks';
import { getButtonActionByActionType } from '@vfit/shared/data-access';
import { useRouter } from 'next/router';
import { IActionSlide, IActionType } from '@vfit/shared/models';
import { IAppQR } from './AppQR.models';
import {
  ButtonContainerDesktop,
  ButtonContainerMobile,
  ButtonContainerTablet,
  Container,
  DescriptionCard,
  DisclaimerContainer,
  MainContainer,
  PlatformContainer,
  QRCode,
  QRCodeCard,
  Store,
  StoreContainer,
  Title,
} from './AppQR.style';

const AppQR = ({
  title,
  titleMobile,
  titleTablet,
  optionList,
  headerImage,
  qrCodeImage,
  disclaimer,
  disclaimerMobile,
  disclaimerTablet,
  downloadList,
  buttons,
  onCustomAppQRAction,
}: IAppQR) => {
  const { push } = useRouter();
  const { isDesktop, isMobile, isTablet } = useDeviceType();
  const [pTitle, pTitleMobile, pTitleTablet, pDisclaimer, pDisclaimerMobile, pDisclaimerTablet] =
    purify([title, titleMobile, titleTablet, disclaimer, disclaimerMobile, disclaimerTablet]);

  const renderDisclaimer = () => {
    if (pDisclaimer && isDesktop) {
      return (
        <DisclaimerContainer
          dangerouslySetInnerHTML={{
            __html: pDisclaimer,
          }}
        />
      );
    }
    if (isMobile && pDisclaimerMobile) {
      return (
        <DisclaimerContainer
          dangerouslySetInnerHTML={{
            __html: pDisclaimerMobile,
          }}
        />
      );
    }
    if (isTablet && pDisclaimerTablet) {
      return (
        <DisclaimerContainer
          dangerouslySetInnerHTML={{
            __html: pDisclaimerTablet,
          }}
        />
      );
    }
    return null;
  };

  const checkAction = (action?: IActionSlide) => {
    if (!action) return;
    if (action?.type == IActionType.LOCAL_URL && action?.localUrl?.includes('$')) {
      onCustomAppQRAction?.(action);
    } else getButtonActionByActionType(action, push);
  };

  const renderButtons = () => {
    if (isDesktop && buttons?.desktopButtons) {
      return (
        (buttons.desktopButtons?.primaryButton?.title ||
          buttons.desktopButtons?.secondaryButton?.title) && (
          <ButtonContainerDesktop>
            {buttons.desktopButtons?.primaryButton?.title && (
              <Button
                style={{ width: 'auto' }}
                variant="primary"
                onClick={() => {
                  checkAction(buttons?.desktopButtons?.primaryButton);
                }}
              >
                {buttons.desktopButtons.primaryButton.title}
              </Button>
            )}
            {buttons.desktopButtons.secondaryButton.title && (
              <Button
                style={{ width: 'auto' }}
                variant="secondary"
                onClick={() => {
                  checkAction(buttons?.desktopButtons?.secondaryButton);
                }}
              >
                {buttons.desktopButtons.secondaryButton.title}
              </Button>
            )}
          </ButtonContainerDesktop>
        )
      );
    }
    if (isTablet && buttons?.tabletButtons) {
      return (
        (buttons.tabletButtons?.primaryButton?.title ||
          buttons.tabletButtons?.secondaryButton?.title) && (
          <ButtonContainerTablet>
            {buttons.tabletButtons?.primaryButton?.title && (
              <Button
                style={{ width: 'auto' }}
                variant="primary"
                onClick={() => {
                  checkAction(buttons?.tabletButtons?.primaryButton);
                }}
              >
                {buttons.tabletButtons.primaryButton.title}
              </Button>
            )}
            {buttons.tabletButtons?.secondaryButton?.title && (
              <Button
                style={{ width: 'auto' }}
                variant="secondary"
                onClick={() => {
                  checkAction(buttons?.tabletButtons?.secondaryButton);
                }}
              >
                {buttons.tabletButtons.secondaryButton.title}
              </Button>
            )}
          </ButtonContainerTablet>
        )
      );
    }
    if (isMobile && buttons?.mobileButtons) {
      return (
        (buttons.mobileButtons?.primaryButton?.title ||
          buttons.mobileButtons?.secondaryButton?.title) && (
          <ButtonContainerMobile>
            {buttons.mobileButtons?.primaryButton?.title && (
              <Button
                style={{ width: 'auto' }}
                variant="primary"
                onClick={() => {
                  checkAction(buttons?.mobileButtons?.primaryButton);
                }}
              >
                {buttons.mobileButtons.primaryButton.title}
              </Button>
            )}
            {buttons.mobileButtons?.secondaryButton?.title && (
              <Button
                style={{ width: 'auto' }}
                variant="secondary"
                onClick={() => {
                  checkAction(buttons?.mobileButtons?.secondaryButton);
                }}
              >
                {buttons.mobileButtons.secondaryButton.title}
              </Button>
            )}
          </ButtonContainerMobile>
        )
      );
    }
    return null;
  };

  const renderTitle = () => {
    if (pTitleMobile && isMobile) {
      return <Title dangerouslySetInnerHTML={{ __html: pTitleMobile }} />;
    }
    if (pTitleTablet && isTablet) {
      return <Title dangerouslySetInnerHTML={{ __html: pTitleTablet }} />;
    }
    if (pTitle) {
      return <Title dangerouslySetInnerHTML={{ __html: pTitle }} />;
    }
    return null;
  };

  return (
    <MainContainer>
      <Container>
        <DescriptionCard>
          {headerImage && (
            <ImageAtoms
              nameCard="appDownload"
              image={headerImage}
              imageMobile={headerImage}
              style={{
                height: '75px',
                width: '280px',
              }}
              isMobile
            />
          )}
          {renderTitle()}
          {optionList && optionList?.length > 0 && (
            <StoreContainer>
              {optionList?.map((option) => (
                <Store>
                  <ImageAtoms
                    nameCard="appDownload"
                    image={option.image}
                    style={{
                      height: '48px',
                      width: '48px',
                    }}
                  />
                  <span>{option?.title}</span>
                </Store>
              ))}
            </StoreContainer>
          )}

          {renderDisclaimer()}

          {isDesktop && downloadList && downloadList.length > 0 && (
            <PlatformContainer>
              {downloadList?.map((el) => (
                <Store>
                  <a href={el?.url || el?.title}>
                    <ImageAtoms
                      nameCard="appDownload"
                      image={el.image}
                      style={{
                        width: '152.5px',
                      }}
                    />
                  </a>
                </Store>
              ))}
            </PlatformContainer>
          )}
          {renderButtons()}
        </DescriptionCard>
        {isDesktop && (
          <QRCodeCard>
            <QRCode>
              <ImageAtoms nameCard="appDownload" image={qrCodeImage} objectFit="cover" />
            </QRCode>
          </QRCodeCard>
        )}
      </Container>
    </MainContainer>
  );
};

export default AppQR;
